import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(ArcElement, Tooltip, Legend);

const GoalSIPCalculator = () => {
  const [goalAmount, setGoalAmount] = useState(5000000);
  const [currentSavings, setCurrentSavings] = useState(100000);
  const [timeframe, setTimeframe] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [inflation, setInflation] = useState(6);
  const [adjustForInflation, setAdjustForInflation] = useState(true);
  const [result, setResult] = useState(null);

  const calculate = () => {
    // Inflation-adjusted goal
    const inflationAdjustedGoal = adjustForInflation 
      ? goalAmount * Math.pow(1 + inflation / 100, timeframe)
      : goalAmount;
    
    // Future value of current savings
    const futureValueCurrentSavings = currentSavings * Math.pow(1 + expectedReturn / 100, timeframe);
    
    // Additional amount needed
    const additionalNeeded = Math.max(0, inflationAdjustedGoal - futureValueCurrentSavings);
    
    // Monthly SIP calculation
    const monthlyReturn = expectedReturn / 12 / 100;
    const months = timeframe * 12;
    
    let monthlySIP = 0;
    if (additionalNeeded > 0) {
      monthlySIP = additionalNeeded * monthlyReturn / (Math.pow(1 + monthlyReturn, months) - 1);
    }
    
    // Step-up SIP (increasing 10% every year)
    let stepUpSIP = 0;
    const stepUpRate = 0.10;
    let targetAmount = additionalNeeded;
    let currentStepSIP = 1000; // Start with initial guess
    
    // Iterative calculation for step-up SIP
    for (let iteration = 0; iteration < 100; iteration++) {
      let accumulated = 0;
      let currentSIP = currentStepSIP;
      
      for (let year = 1; year <= timeframe; year++) {
        for (let month = 1; month <= 12; month++) {
          accumulated = (accumulated + currentSIP) * (1 + monthlyReturn);
        }
        currentSIP *= (1 + stepUpRate);
      }
      
      if (Math.abs(accumulated - targetAmount) < 100) break;
      currentStepSIP = currentStepSIP * (targetAmount / accumulated);
    }
    stepUpSIP = Math.max(0, currentStepSIP);
    
    // Total investment
    const totalInvestment = monthlySIP * months + currentSavings;
    const wealthGained = inflationAdjustedGoal - totalInvestment;

    setResult({
      originalGoal: goalAmount,
      inflationAdjustedGoal,
      futureValueCurrentSavings,
      additionalNeeded,
      monthlySIP,
      stepUpSIP,
      totalInvestment,
      wealthGained,
      yearlySIP: monthlySIP * 12,
      totalSIPInvestment: monthlySIP * months,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Goal-based SIP Planning', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Parameter', 'Value']],
      body: [
        ['Financial Goal', formatCurrencyForPDF(result.originalGoal)],
        ['Inflation Adjusted Goal', formatCurrencyForPDF(result.inflationAdjustedGoal)],
        ['Time Frame', `${timeframe} years`],
        ['Expected Return', `${expectedReturn}% p.a.`],
        ['Current Savings', formatCurrencyForPDF(currentSavings)],
        ['Current Savings Will Grow To', formatCurrencyForPDF(result.futureValueCurrentSavings)],
        ['Additional Amount Needed', formatCurrencyForPDF(result.additionalNeeded)],
        ['Monthly SIP Required', formatCurrencyForPDF(result.monthlySIP)],
        ['Step-up SIP (10% yearly increase)', formatCurrencyForPDF(result.stepUpSIP)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Goal_SIP_Plan.pdf');
  };

  const chartData = result ? {
    labels: ['Current Savings (FV)', 'SIP Investment', 'Wealth Gained'],
    datasets: [{
      data: [
        result.futureValueCurrentSavings,
        result.totalSIPInvestment,
        Math.max(0, result.wealthGained)
      ],
      backgroundColor: ['#3b82f6', '#f59e0b', '#10b981'],
      borderWidth: 0,
    }]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Goal SIP Calculator"
        description="Free Goal SIP Calculator - Calculate monthly SIP needed to reach your financial goal."
        keywords="goal SIP calculator, SIP goal calculator, target SIP calculator, financial goal planner, SIP for goal"
        canonicalPath="/goal-sip"
      />
      <div className="calculator-header">
        <h1>🎯 Goal SIP Calculator</h1>
        <p>Calculate the SIP needed to achieve your financial goals</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Your Financial Goal (₹)</label>
            <input
              type="number"
              value={goalAmount === '' ? '' : goalAmount}
              onChange={(e) => setGoalAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>
              E.g., Child's education, House down payment, Car, Vacation
            </small>
          </div>

          <div className="input-group">
            <label>Current Savings Towards Goal (₹)</label>
            <input
              type="number"
              value={currentSavings === '' ? '' : currentSavings}
              onChange={(e) => setCurrentSavings(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Time to Achieve (Years)</label>
              <input
                type="number"
                value={timeframe === '' ? '' : timeframe}
                onChange={(e) => setTimeframe(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Expected Return (% p.a.)</label>
              <input
                type="number"
                step="0.5"
                value={expectedReturn === '' ? '' : expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={adjustForInflation}
                onChange={(e) => setAdjustForInflation(e.target.checked)}
                style={{ width: 'auto' }}
              />
              Adjust goal for inflation
            </label>
          </div>

          {adjustForInflation && (
            <div className="input-group">
              <label>Inflation Rate (% p.a.)</label>
              <input
                type="number"
                step="0.5"
                value={inflation === '' ? '' : inflation}
                onChange={(e) => setInflation(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          )}

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate SIP Required
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                <h3>Monthly SIP Required</h3>
                <div className="amount">{formatCurrency(result.monthlySIP)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Or start with {formatCurrency(result.stepUpSIP)}/month (with 10% yearly increase)
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Your Goal</span>
                  <span className="value">{formatCurrency(result.originalGoal)}</span>
                </div>
                {adjustForInflation && (
                  <div className="result-item" style={{ background: '#fef3c7' }}>
                    <span className="label">Inflation Adjusted Goal</span>
                    <span className="value" style={{ color: '#f59e0b' }}>{formatCurrency(result.inflationAdjustedGoal)}</span>
                  </div>
                )}
                <div className="result-item">
                  <span className="label">Current Savings Will Grow To</span>
                  <span className="value">{formatCurrency(result.futureValueCurrentSavings)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Additional Amount Needed</span>
                  <span className="value">{formatCurrency(result.additionalNeeded)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Monthly SIP Required</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.monthlySIP)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Yearly Investment</span>
                  <span className="value">{formatCurrency(result.yearlySIP)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Investment</span>
                  <span className="value">{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Wealth Gained</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.wealthGained)}</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Goal Achievement Breakdown</h4>
                  <Pie data={chartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download Goal Plan PDF
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your goal details to calculate required SIP</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 Goal Planning Tips</h4>
        <ul>
          <li>Use equity funds for goals 5+ years away (higher returns)</li>
          <li>Use debt funds/FD for goals within 3 years (capital protection)</li>
          <li>Start step-up SIP to match income growth</li>
          <li>Review and rebalance annually</li>
        </ul>
      </div>
    </div>
  );
};

export default GoalSIPCalculator;
